import React, { useState, useEffect } from 'react';
import { getFriendliesSettings, updateFriendliesSettings } from '../api';

export default function FriendliesSettingsTab() {
  const [settings, setSettings] = useState({ enabled: false, friendliesUrl: '', apiKey: '', transactionsPerSuccess: 2, visaPercentage: 80, mcPercentage: 20, pricePoints: [{ price: 3.99, percentage: 60 }, { price: 6.99, percentage: 30 }, { price: 9.99, percentage: 10 }] });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    getFriendliesSettings().then(r => { if (r.success) setSettings(r.data); }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleToggle = async () => {
    setSaving(true); setMsg('');
    try { const r = await updateFriendliesSettings({ enabled: !settings.enabled }); if (r.success) { setSettings(r.data); setMsg('Saved!'); } } catch (e) { setMsg(e.message); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 2000); }
  };

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const r = await updateFriendliesSettings(settings);
      if (r.success) { setSettings(r.data); setMsg('✅ Settings saved!'); }
    } catch (e) { setMsg('❌ ' + e.message); }
    finally { setSaving(false); setTimeout(() => setMsg(''), 3000); }
  };

  const updatePP = (i, field, val) => {
    const pp = [...settings.pricePoints];
    pp[i] = { ...pp[i], [field]: parseFloat(val) || 0 };
    setSettings({ ...settings, pricePoints: pp });
  };
  const addPP = () => setSettings({ ...settings, pricePoints: [...settings.pricePoints, { price: 1.99, percentage: 0 }] });
  const removePP = (i) => { if (settings.pricePoints.length <= 1) return; setSettings({ ...settings, pricePoints: settings.pricePoints.filter((_, idx) => idx !== i) }); };
  const ppTotal = settings.pricePoints.reduce((s, p) => s + (p.percentage || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">🇺🇸 Friendlies USA — Auto Transactions</h2>
          <p className="text-xs text-slate-400 mt-1">Send automatic USA transactions to Friendlies Monitoring on every Rebill/Recycle success</p>
        </div>
        <button onClick={handleToggle} disabled={saving}
          className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-colors ${settings.enabled ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600/80 hover:bg-red-600 text-white'}`}>
          {settings.enabled ? '✅ ENABLED' : '❌ DISABLED'}
        </button>
      </div>

      {msg && <div className={`px-4 py-2 rounded-lg text-sm ${msg.includes('❌') ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>{msg}</div>}

      {/* Connection Settings */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-4">
        <h3 className="text-lg font-semibold text-white">🔗 Connection</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Friendlies Monitoring URL</label>
            <input type="text" value={settings.friendliesUrl} onChange={e => setSettings({ ...settings, friendliesUrl: e.target.value })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none focus:border-blue-500" placeholder="https://fuzetransac.com" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">API Key</label>
            <input type="password" value={settings.apiKey} onChange={e => setSettings({ ...settings, apiKey: e.target.value })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none focus:border-blue-500" placeholder="secret-key" />
          </div>
        </div>
      </div>

      {/* Transaction Config */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-4">
        <h3 className="text-lg font-semibold text-white">⚡ Transaction Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Transactions per Success</label>
            <input type="number" min="1" max="10" value={settings.transactionsPerSuccess}
              onChange={e => setSettings({ ...settings, transactionsPerSuccess: parseInt(e.target.value) || 2 })}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none focus:border-blue-500" />
            <p className="text-[10px] text-slate-500 mt-1">Number of USA transactions per rebill success</p>
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">VISA %</label>
            <input type="number" min="0" max="100" value={settings.visaPercentage}
              onChange={e => { const v = parseInt(e.target.value) || 0; setSettings({ ...settings, visaPercentage: Math.min(100, v), mcPercentage: 100 - Math.min(100, v) }); }}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none focus:border-blue-500" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Mastercard %</label>
            <input type="number" min="0" max="100" value={settings.mcPercentage}
              onChange={e => { const v = parseInt(e.target.value) || 0; setSettings({ ...settings, mcPercentage: Math.min(100, v), visaPercentage: 100 - Math.min(100, v) }); }}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm outline-none focus:border-blue-500" />
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="w-full bg-slate-700 rounded-full h-3 overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${settings.visaPercentage}%` }} />
          </div>
          <span className="text-xs text-slate-400 whitespace-nowrap">VISA {settings.visaPercentage}% / MC {settings.mcPercentage}%</span>
        </div>
      </div>

      {/* Price Points */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">💲 Price Points</h3>
          <button onClick={addPP} className="px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">+ Add</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {settings.pricePoints.map((pp, i) => (
            <div key={i} className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-blue-400">Price Point #{i + 1}</span>
                {settings.pricePoints.length > 1 && <button onClick={() => removePP(i)} className="text-red-400 text-xs hover:text-red-300">✕</button>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-slate-500">Price ($)</label>
                  <input type="number" step="0.01" min="0.01" value={pp.price} onChange={e => updatePP(i, 'price', e.target.value)}
                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-slate-500">Percentage (%)</label>
                  <input type="number" min="0" max="100" value={pp.percentage} onChange={e => updatePP(i, 'percentage', e.target.value)}
                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm outline-none" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <p className={`text-xs ${ppTotal === 100 ? 'text-green-400' : 'text-red-400'}`}>
            Total: {ppTotal}% {ppTotal === 100 ? '✅' : `(should be 100%)`}
          </p>
          <p className="text-[10px] text-slate-500">
            Distribution: {settings.pricePoints.map(pp => `$${pp.price} → ${pp.percentage}%`).join(' | ')}
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
        <h4 className="text-sm font-bold text-slate-300 mb-2">📊 Summary</h4>
        <p className="text-xs text-slate-400">
          When a Rebill or Recycle transaction succeeds → <span className="text-blue-400 font-bold">{settings.transactionsPerSuccess}</span> USA transaction(s) will be sent to
          <span className="text-cyan-400 font-mono ml-1">{settings.friendliesUrl || '(not configured)'}</span> on the <span className="text-orange-400 font-bold">same gateway</span>.
        </p>
        <p className="text-xs text-slate-400 mt-1">
          Card distribution: <span className="text-blue-400">{settings.visaPercentage}% VISA</span> / <span className="text-orange-400">{settings.mcPercentage}% MC</span> —
          Prices: {settings.pricePoints.map(pp => <span key={pp.price} className="text-green-400 mx-0.5">${pp.price}({pp.percentage}%)</span>)}
        </p>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving || ppTotal !== 100}
          className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          {saving ? '⏳ Saving...' : '💾 Save All Settings'}
        </button>
      </div>
    </div>
  );
}
